import * as Sentry from '@sentry/nextjs';
import { isAxiosError } from 'axios';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { matchMessageNoiseRule } from '@/shared/lib/errors/noise-rules';

type Severity = 'fatal' | 'error' | 'warning' | 'info';

interface CaptureContext {
  component: string;
  action: string;
  severity?: Severity;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  /**
   * Sentry grouping override. Default grouping is by stack, so every failure of
   * one call site lands in one issue whatever its status — pass a status class
   * when 4xx noise would otherwise bury real 5xx.
   */
  fingerprint?: string[];
  /** Skip PostHog for low-severity browser API noise (autoplay, clipboard, localStorage) */
  skipPostHog?: boolean;
}

// Lazy PostHog — resolved once, cached at module level
let _posthog: { capture: (event: string, props?: Record<string, unknown>) => void } | null = null;
let _posthogResolved = false;

function getPostHog() {
  if (_posthogResolved) return _posthog;
  _posthogResolved = true;
  if (typeof window !== 'undefined') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      _posthog = require('posthog-js').default;
    } catch {
      _posthog = null;
    }
  }
  return _posthog;
}

export function errorMessageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Properties for the PostHog `client_error` event. Noise reaches PostHog two
 * ways: an explicit `tags.signal` from the call site (CLAUDE.md noise rule)
 * or a message match in MESSAGE_NOISE_RULES — the same source
 * `tagNoiseExceptions` uses for `$exception`. Either way the event is tagged,
 * never dropped, so the hourly creator-pipeline alert can exclude it while
 * spike detection still sees it.
 */
export function buildClientErrorProps(
  error: unknown,
  ctx: CaptureContext,
  extra: Record<string, unknown>
): Record<string, unknown> {
  const message = errorMessageOf(error);
  const severity = ctx.severity ?? 'error';
  const { signal: tagSignal, noise_rule: tagRule, ...otherTags } = ctx.tags ?? {};
  const ruleId = tagSignal === 'noise' ? (tagRule ?? 'call-site') : matchMessageNoiseRule(message);
  const noise = ruleId ? { signal: 'noise', noise_rule: ruleId } : {};

  return {
    error_message: message,
    component: ctx.component,
    action: ctx.action,
    severity,
    ...otherTags,
    ...noise,
    ...extra,
  };
}

/**
 * Capture an error to Sentry (always) and PostHog (client-side, unless skipped).
 * Replaces bare `console.error` patterns throughout the codebase.
 *
 * Usage:
 *   captureError(error, { component: 'UploadModal', action: 'fetch-tags' });
 *   captureError(error, { component: 'ShakaPlayer', action: 'init', severity: 'warning', skipPostHog: true });
 */
export function captureError(error: unknown, ctx: CaptureContext): void {
  const severity = ctx.severity ?? 'error';

  const extra: Record<string, unknown> = { ...ctx.extra };
  if (isAxiosError(error) && error.response) {
    extra.responseStatus = error.response.status;
    extra.responseData = error.response.data;
    extra.requestUrl = error.config?.url;
  }

  Sentry.captureException(error, {
    level: severity,
    tags: { component: ctx.component, action: ctx.action, ...ctx.tags },
    ...(Object.keys(extra).length > 0 ? { extra } : {}),
    ...(ctx.fingerprint ? { fingerprint: ctx.fingerprint } : {}),
  });

  if (!ctx.skipPostHog) {
    const ph = getPostHog();
    ph?.capture(AnalyticsEvent.CLIENT_ERROR, buildClientErrorProps(error, ctx, extra));
  }

  if (process.env.NODE_ENV === 'development') {
    console.error(`[${ctx.component}:${ctx.action}]`, error);
  }
}
